import { call, put, select, all, takeEvery } from 'redux-saga/effects';
import { NOTIFY_LEVEL, RequestStatus, SHOW_NOTIFY } from 'constants';
import {
  GET_ACTORS_ACCOUNTS,
  GET_ACCOUNT,
  CREATE_ACTORS_ACCOUNT,
  BULK_CREATE_ACCOUNTS,
  REMOVE_ACTORS_ACCOUNT,
  LOCK_ACTORS_ACCOUNT,
  UPDATE_ACTORS_ACCOUNT,
  CREATE_TRANSACTION,
  GET_CHILD_TRANSACTIONS,
  CREATE_GROUP_TRANSACTION,
  GET_TRANSACTIONS,
  SEARCH_TRANSACTIONS,
  REMOVE_TRANSACTION,
  GET_ACCOUNT_DETAILS,
  SET_ACCOUNT_FORMULA,
  GET_ACCOUNT_FORMULA_INFO,
  SET_FORMULAS,
  GET_SINGLE_TRANSACTION,
} from '@control-front-end/common/constants/actorAccounts';
import { createAccountCurrencies } from '@control-front-end/common/sagas/accountCurrencies';
import { createAccountName } from '@control-front-end/common/sagas/accountNames';
import api from '@control-front-end/common/sagas/api';
import AppUtils from '@control-front-end/utils/utils';

/**
 * Обновить счет
 */
export function* updateAccount({ accountType, key, incomeType, data }) {
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = actorsAccs.slice();
  const accIndex = copyAccounts.findIndex((i) => i.key === key);
  const accModel = copyAccounts[accIndex];
  if (incomeType === 'all') {
    Object.assign(accModel, data.main);
    Object.assign(accModel.debit, data.debit);
    Object.assign(accModel.credit, data.credit);
  } else Object.assign(accModel[incomeType], data);
  copyAccounts.splice(accIndex, 1, accModel);
  return copyAccounts;
}

/**
 * Создать модель сущности
 */
function createAccountsModels(accounts) {
  accounts.forEach((acc) => {
    acc.key = `${acc.nameId}+${acc.currencyId}`;
  });
  const groupAccounts = AppUtils.groupBy(accounts, 'key');
  return Object.keys(groupAccounts).map((key) => {
    const accs = groupAccounts[key];
    const { currencyId, currencyName, accountName, nameId, status, isSystem } =
      accs[0];
    const nAcc = {
      key,
      currencyId: currencyId.toString(),
      currencyName,
      accountName,
      nameId,
      status,
      isSystem,
    };
    accs.forEach((i) => {
      i.availableAmount = AppUtils.isUndefined(i.availableAmount)
        ? i.amount
        : i.availableAmount;
      i.holdAmount = i.holdAmount || 0;
      nAcc[i.incomeType] = i;
      if (i.connector) {
        const { picture, formPicture } = i.connector;
        let pictureUrl = picture
          ? AppUtils.makeAppUrl(`/download/${picture}`)
          : null;
        if (!pictureUrl && formPicture && formPicture.includes('static'))
          pictureUrl = formPicture;
        i.connector.pictureUrl = pictureUrl;
      }
    });
    return nAcc;
  });
}

/**
 * Создать модель транзакций
 * @param list
 * @param entity
 * @param incomeType
 */
function makeTransactionModel({ list, debit, credit, incomeType }) {
  const accounts = { debit, credit };
  let total = incomeType
    ? accounts[incomeType].amount
    : -debit.amount + credit.amount;

  list.forEach((item) => {
    item.total = total;
    if (!incomeType) {
      const diff = item.incomeType === 'credit' ? -item.amount : item.amount;
      total += diff;
    } else {
      total -= item.amount;
    }
  });
}

/**
 * Получить список счетов актора
 */
function* getActorsAccounts({ payload, callback }) {
  const { actorId, accountType = 'fact', localState } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_accounts/${actorId}`,
    queryParams: { accountType },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const list = yield call(createAccountsModels, data.data);
  if (!localState) {
    yield put({
      type: GET_ACTORS_ACCOUNTS.SUCCESS,
      payload: { [accountType]: [...list] },
    });
  }
  if (callback) callback(list);
}

/**
 * Получить счет по id
 */
function* getAccount({ payload }) {
  const { id, key, accountType = 'fact' } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_accounts/single/${id}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const accountData = data.data;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = actorsAccs.slice();
  const accIndex = copyAccounts.findIndex((acc) => acc.key === key);
  const accModel = copyAccounts[accIndex];
  accModel[accountData.incomeType] = accountData;
  copyAccounts.splice(accIndex, 1, accModel);
  yield put({
    type: GET_ACTORS_ACCOUNTS.SUCCESS,
    payload: { [accountType]: copyAccounts },
  });
}

/**
 * Создать счет
 * @param payload
 * @param callback
 */
function* createActorsAccounts({ payload, callback }) {
  const {
    actorId,
    accountType,
    ignoreIfExist,
    treeCalculation,
    minLimit,
    maxLimit,
    search,
    accName,
    currencyName,
    actionCurrency,
    actionName,
  } = payload;
  let { currencyId, nameId } = payload;
  if (actionCurrency === 'create') {
    const currencyModel = yield call(createAccountCurrencies, {
      payload: currencyName,
    });
    currencyId = currencyModel.id;
  }
  if (actionName === 'create') {
    const accNameModel = yield call(createAccountName, { payload: accName });
    nameId = accNameModel.id;
  }
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/actors_accounts/${actorId}`,
    queryParams: { ignoreIfExist },
    body: {
      accountType,
      currencyId,
      nameId,
      treeCalculation,
      minLimit,
      maxLimit,
      search,
    },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = actorsAccs.slice();
  const newOwnAccounts = data.data.filter((acc) => acc.actorId === actorId);
  const list = yield call(createAccountsModels, newOwnAccounts);
  copyAccounts.unshift(...list);
  yield put({
    type: CREATE_ACTORS_ACCOUNT.SUCCESS,
    payload: { [accountType]: copyAccounts },
  });
  if (callback) callback(data.data);
}

/**
 * Создать массово счета
 * @param payload
 * @param callback
 */
function* bulkCreateAccounts({ payload, callback }) {
  const {
    actors,
    accountType,
    accName,
    currencyName,
    actionCurrency,
    actionName,
    treeCalculation,
    minLimit,
    maxLimit,
    search,
  } = payload;
  let { currencyId, nameId } = payload;
  if (actionCurrency === 'create') {
    const currencyModel = yield call(createAccountCurrencies, {
      payload: currencyName,
    });
    currencyId = currencyModel.id;
  }
  if (actionName === 'create') {
    const accNameModel = yield call(createAccountName, { payload: accName });
    nameId = accNameModel.id;
  }
  const { result, data } = yield call(api, {
    method: 'post',
    url: '/actors_accounts',
    body: {
      nameId,
      accountType,
      currencyId,
      actors,
      treeCalculation,
      minLimit,
      maxLimit,
      search,
    },
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (callback) callback(data.data);
}

/**
 * Удалить счет
 */
function* removeActorsAccounts({ payload, callback }) {
  const { actorId, nameId, currencyId, key, accountType = 'fact' } = payload;
  const { result, data } = yield call(api, {
    method: 'delete',
    url: `/actors_accounts/${actorId}/${currencyId}/${nameId}/${accountType}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = actorsAccs.filter((i) => i.key !== key);
  yield put({
    type: REMOVE_ACTORS_ACCOUNT.SUCCESS,
    payload: { [accountType]: copyAccounts },
  });
  if (callback) callback(data.data);
}

/**
 * Обновить счета
 */
function* updateActorsAccounts({ payload, callback }) {
  const {
    actorId,
    nameId,
    currencyId,
    key,
    accountType = 'fact',
    treeCalculation,
    minLimit,
    maxLimit,
    search,
  } = payload;
  const body = {
    treeCalculation,
    minLimit,
    maxLimit,
    search,
  };
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/actors_accounts/${actorId}/${currencyId}/${nameId}/${accountType}`,
    body,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const accIndex = actorsAccs.findIndex((i) => i.key === key);
  const findAcc = actorsAccs[accIndex];
  Object.assign(findAcc.debit, body);
  Object.assign(findAcc.credit, body);
  actorsAccs.splice(accIndex, 1, findAcc);
  yield put({
    type: UPDATE_ACTORS_ACCOUNT.SUCCESS,
    payload: { [accountType]: actorsAccs },
  });
  if (callback) callback(data.data);
}

/**
 * Блокировать/разблокировать счет
 */
function* lockActorsAccounts({ payload, callback }) {
  const { actorId, nameId, currencyId, accountType, status, key } = payload;
  const body = {
    nameId,
    currencyId,
    type: accountType,
    status,
  };
  const { result, data } = yield call(api, {
    method: 'put',
    url: `/actors_accounts/block/${actorId}`,
    body,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const setStatus = {
    main: { status },
    debit: { status },
    credit: { status },
  };
  const actorsAccs = yield updateAccount({
    accountType,
    key,
    incomeType: 'all',
    data: setStatus,
  });
  yield put({
    type: LOCK_ACTORS_ACCOUNT.SUCCESS,
    payload: { [accountType]: actorsAccs },
  });
  if (callback) callback(data.data);
}

/**
 * Создать транзакцию
 */
function* createTransaction({ payload, callback }) {
  const { id, transaction, key, incomeType, accountType } = payload;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/transactions/${id}`,
    body: transaction,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = structuredClone(actorsAccs);
  const accIndex = copyAccounts.findIndex((i) => i.key === key);
  const accModel = copyAccounts[accIndex];
  if (data.data.type === 'declined') {
    yield put({
      type: SHOW_NOTIFY.REQUEST,
      payload: {
        id: AppUtils.createRid(),
        type: NOTIFY_LEVEL.ERROR,
        label: 'Transaction has been declined',
      },
    });
  } else {
    accModel[incomeType].amount += +transaction.amount;
    accModel[incomeType].availableAmount += +transaction.amount;
    copyAccounts.splice(accIndex, 1, accModel);
  }
  yield put({
    type: CREATE_TRANSACTION.SUCCESS,
    payload: { [accountType]: copyAccounts },
  });
  if (callback) callback(data.data);
}

/**
 * Создать групповую транзакцию
 */
function* createGroupTransaction({ payload, callback }) {
  const { id, transaction } = payload;
  transaction.zoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { result } = yield call(api, {
    method: 'post',
    url: `/transactions/group/${id}`,
    body: transaction,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (callback) callback();
}

/**
 * Получить транзакции счета
 */
function* getTransactions({ payload, callback }) {
  const {
    actorId,
    nameId,
    currencyId,
    transactions,
    loadMore,
    accountType,
    incomeType,
    debit,
    credit,
    from,
    to,
    onlyRoot = true,
  } = payload;
  const { endList, list, limit, offset } = transactions;
  if (endList) return;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/transactions/${actorId}`,
    queryParams: {
      limit,
      offset,
      incomeType,
      accountType,
      nameId,
      currencyId,
      from,
      to,
      onlyRoot,
    },
  });
  if (result !== RequestStatus.SUCCESS) return;
  const oldList = list.slice();
  const newList = loadMore ? oldList.concat(data.data) : data.data;
  makeTransactionModel({ list: newList, debit, credit, incomeType });
  if (callback) {
    callback({
      list: newList,
      offset: offset + limit,
      limit,
      endList: !data.data.length,
    });
  }
}

/**
 * Получить дочерние транзакции
 */
function* getChildTransactions({ payload, callback }) {
  const { transactionId } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/transactions/children/${transactionId}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (callback) callback(data.data);
}

/**
 * Получить транзакцию
 */
function* getSingleTransaction({ payload, callback }) {
  const { transactionId } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/transactions/single/${transactionId}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (callback) callback(data.data);
}

/**
 * Поиск транзакций
 */
function* searchTransactions({ payload, callback }) {
  const { actorId, q, incomeType, accountType, nameId, currencyId } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/transactions/search/${actorId}`,
    queryParams: { incomeType, accountType, nameId, currencyId, q },
  });
  if (result !== RequestStatus.SUCCESS) return;
  if (callback) {
    callback(data.data);
  }
}

/**
 * Удаление транзакции
 */
function* removeTransaction({ payload, callback }) {
  const { id, transactions, accountType, nameId, currencyId } = payload;
  const { endList, list, limit, offset } = transactions;
  const { result } = yield call(api, {
    method: 'delete',
    url: `/transactions/${id}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  const copyList = list.slice();
  const findIndex = copyList.findIndex((i) => i.id === id);
  const transaction = copyList[findIndex];
  if (findIndex !== -1) copyList.splice(findIndex, 1);
  if (callback) {
    callback({
      list: copyList,
      offset: offset - 1,
      limit,
      endList,
    });
  }
  const { incomeType, amount } = transaction;
  const key = `${nameId}+${currencyId}`;
  const actorsAccs = yield select((state) => state.actorsAccounts[accountType]);
  const copyAccounts = structuredClone(actorsAccs);
  const accIndex = copyAccounts.findIndex((i) => i.key === key);
  const accModel = copyAccounts[accIndex];
  accModel[incomeType].amount -= +amount;
  accModel[incomeType].availableAmount -= +amount;
  copyAccounts.splice(accIndex, 1, accModel);
  yield put({
    type: REMOVE_TRANSACTION.SUCCESS,
    payload: { [accountType]: copyAccounts },
  });
}

/**
 * Получить детали счета
 */
function* getAccountDetails({ payload, callback }) {
  const { actorId, currencyId, nameId } = payload;
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_accounts/all_children/${actorId}`,
    queryParams: { currencyId, nameId },
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({ type: GET_ACCOUNT_DETAILS.SUCCESS, payload: data.data });
  if (callback) callback(data.data);
}

/**
 * Добавить\удалить формулу расчета баланса
 */
function* setAccountFormula({ payload, callback }) {
  const { id, key, incomeType, accountType, formula } = payload;
  const { result, data } = yield call(api, {
    method: 'post',
    url: `/actors_accounts/formula/${id}`,
    body: { formula },
    handleErrCodes: [400],
  });
  if (result === RequestStatus.SUCCESS) {
    const copyAccounts = yield updateAccount({
      accountType,
      key,
      incomeType,
      data: { formula, amount: data.data.amount },
    });
    yield put({
      type: SET_ACCOUNT_FORMULA.SUCCESS,
      payload: { [accountType]: copyAccounts },
    });
    if (callback) callback(data.data);
  }
  return { result, data };
}

/**
 * Добавить\удалить формулы для credit и debit
 */
function* setCreditDebitFormulas({ payload, formActions, callback }) {
  const { credit, debit, accountType, key } = payload;
  const [debitRes, creditRes] = yield all([
    call(setAccountFormula, {
      payload: { ...debit, key, incomeType: 'debit', accountType },
    }),
    call(setAccountFormula, {
      payload: { ...credit, key, incomeType: 'credit', accountType },
    }),
  ]);
  formActions.setSubmitting(false);
  if (
    [debitRes.result, creditRes.result].every(
      (i) => i === RequestStatus.SUCCESS
    )
  ) {
    callback();
  } else {
    const { data: dData } = debitRes;
    const { data: cData } = creditRes;
    if (dData.statusCode === 400)
      formActions.setErrors({ debitFormula: dData.message });
    if (cData.statusCode === 400)
      formActions.setErrors({ creditFormula: cData.message });
  }
}

/**
 * Получить информацию по формуле счета
 */
function* getAccountFormulaInfo({ payload: id, callback }) {
  const { result, data } = yield call(api, {
    method: 'get',
    url: `/actors_accounts/formula_info/${id}`,
  });
  if (result !== RequestStatus.SUCCESS) return;
  yield put({ type: GET_ACCOUNT_FORMULA_INFO.SUCCESS, payload: data.data });
  if (callback) callback(data.data);
}

function* actorsAccounts() {
  yield takeEvery(GET_ACTORS_ACCOUNTS.REQUEST, getActorsAccounts);
  yield takeEvery(BULK_CREATE_ACCOUNTS.REQUEST, bulkCreateAccounts);
  yield takeEvery(CREATE_ACTORS_ACCOUNT.REQUEST, createActorsAccounts);
  yield takeEvery(REMOVE_ACTORS_ACCOUNT.REQUEST, removeActorsAccounts);
  yield takeEvery(LOCK_ACTORS_ACCOUNT.REQUEST, lockActorsAccounts);
  yield takeEvery(UPDATE_ACTORS_ACCOUNT.REQUEST, updateActorsAccounts);
  yield takeEvery(CREATE_TRANSACTION.REQUEST, createTransaction);
  yield takeEvery(REMOVE_TRANSACTION.REQUEST, removeTransaction);
  yield takeEvery(CREATE_GROUP_TRANSACTION.REQUEST, createGroupTransaction);
  yield takeEvery(GET_TRANSACTIONS.REQUEST, getTransactions);
  yield takeEvery(GET_SINGLE_TRANSACTION.REQUEST, getSingleTransaction);
  yield takeEvery(GET_CHILD_TRANSACTIONS.REQUEST, getChildTransactions);
  yield takeEvery(SEARCH_TRANSACTIONS.REQUEST, searchTransactions);
  yield takeEvery(SET_ACCOUNT_FORMULA.REQUEST, setAccountFormula);
  yield takeEvery(SET_FORMULAS.REQUEST, setCreditDebitFormulas);
  yield takeEvery(GET_ACCOUNT_FORMULA_INFO.REQUEST, getAccountFormulaInfo);
  yield takeEvery(GET_ACCOUNT_DETAILS.REQUEST, getAccountDetails);
  yield takeEvery(GET_ACCOUNT.REQUEST, getAccount);
}

export default actorsAccounts;
